import React, { useEffect, useRef, useState } from "react";
import { PencilSimple, Eraser, Trash, X } from "@phosphor-icons/react";

const COLORS = ["#22d3ee", "#f43f5e", "#f59e0b", "#a3e635", "#ffffff", "#8b5cf6"];

// Collaborative whiteboard. Draw ops relay over the same meeting WebSocket.
export function Whiteboard({ send, onReady, onClose }) {
  const canvasRef = useRef(null);
  const ctxRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef(null);
  const [color, setColor] = useState("#22d3ee");
  const [size, setSize] = useState(3);
  const [erase, setErase] = useState(false);

  const draw = (x0, y0, x1, y1, c, w, isErase) => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    ctx.globalCompositeOperation = isErase ? "destination-out" : "source-over";
    ctx.strokeStyle = c;
    ctx.lineWidth = isErase ? w * 6 : w;
    ctx.lineCap = "round";
    ctx.beginPath();
    ctx.moveTo(x0, y0);
    ctx.lineTo(x1, y1);
    ctx.stroke();
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const rect = canvas.parentElement.getBoundingClientRect();
    canvas.width = rect.width;
    canvas.height = rect.height;
    ctxRef.current = canvas.getContext("2d");
    // receive remote ops
    onReady?.((op) => {
      if (op.type === "clear") { ctxRef.current.clearRect(0, 0, canvas.width, canvas.height); return; }
      draw(op.x0 * canvas.width, op.y0 * canvas.height, op.x1 * canvas.width, op.y1 * canvas.height, op.c, op.w, op.e);
    });
    return () => onReady?.(null);
  }, []); // eslint-disable-line

  const pos = (e) => {
    const r = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return { x: t.clientX - r.left, y: t.clientY - r.top };
  };
  const start = (e) => { drawing.current = true; last.current = pos(e); };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const p = pos(e);
    const canvas = canvasRef.current;
    draw(last.current.x, last.current.y, p.x, p.y, color, size, erase);
    send?.({ type: "draw", x0: last.current.x / canvas.width, y0: last.current.y / canvas.height,
      x1: p.x / canvas.width, y1: p.y / canvas.height, c: color, w: size, e: erase });
    last.current = p;
  };
  const end = () => { drawing.current = false; };
  const clear = () => {
    const c = canvasRef.current;
    ctxRef.current.clearRect(0, 0, c.width, c.height);
    send?.({ type: "clear" });
  };

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-white">
      <div className="flex items-center gap-2 border-b border-black/10 bg-white px-3 py-2">
        <span className="mr-2 text-sm font-semibold text-ink" style={{ color: "#08111F" }}>Whiteboard</span>
        {COLORS.map((c) => (
          <button key={c} data-testid={`wb-color-${c}`} onClick={() => { setColor(c); setErase(false); }}
            className={`h-6 w-6 rounded-full border-2 ${color === c && !erase ? "border-black" : "border-transparent"}`}
            style={{ background: c }} />
        ))}
        <input type="range" min="1" max="12" value={size} onChange={(e) => setSize(+e.target.value)} className="ml-2 w-24" />
        <button data-testid="wb-pen" onClick={() => setErase(false)} className={`ml-2 rounded-lg p-2 ${!erase ? "bg-black/10" : ""}`} style={{ color: "#08111F" }}><PencilSimple size={18} /></button>
        <button data-testid="wb-eraser" onClick={() => setErase(true)} className={`rounded-lg p-2 ${erase ? "bg-black/10" : ""}`} style={{ color: "#08111F" }}><Eraser size={18} /></button>
        <button data-testid="wb-clear" onClick={clear} className="rounded-lg p-2 text-red-500"><Trash size={18} /></button>
        <button data-testid="wb-close" onClick={onClose} className="ml-auto rounded-lg p-2" style={{ color: "#08111F" }}><X size={18} /></button>
      </div>
      <div className="relative flex-1">
        <canvas ref={canvasRef} data-testid="wb-canvas" className="absolute inset-0 h-full w-full touch-none cursor-crosshair"
          onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
          onTouchStart={start} onTouchMove={move} onTouchEnd={end} />
      </div>
    </div>
  );
}
